import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { trpcClient } from "@/lib/trpc";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calculator, Package, RefreshCw, Clock, Layers, CheckCircle2, ArrowRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber, formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { KpiStatCard } from "@/components/shared/KpiStatCard";
import { ERPPagination } from "@/components/shared/ERPPagination";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

export default function EstimatingDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCalculating, setIsCalculating] = useState<string | null>(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);

  const {
    data: pendingDetails = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["estimating-dashboard", "pending-pricing"],
    queryFn: async () => {
      const res = await trpcClient.quotations.listPendingPricing.query();
      return (res as any) || [];
    },
  });

  const totalItems = pendingDetails.length;
  const totalQuantity = useMemo(
    () => pendingDetails.reduce((acc: number, d: any) => acc + Number(d.quantity || 0), 0),
    [pendingDetails]
  );
  const estimatingCount = useMemo(
    () => pendingDetails.filter((d: any) => d.pricingStatus === "ESTIMATING").length,
    [pendingDetails]
  );
  const pendingCount = totalItems - estimatingCount;

  const paginatedDetails = useMemo(() => {
    const from = (page - 1) * pageSize;
    return pendingDetails.slice(from, from + pageSize);
  }, [pendingDetails, page, pageSize]);

  const handleCalculate = async (detailId: string) => {
    try {
      setIsCalculating(detailId);
      toast.loading("Đang tính giá tự động...", { id: `calc-${detailId}` });
      await trpcClient.quotations.calculateCostEstimate.mutate({
        detailId,
        batchSizeSheets: 1000,
      });
      toast.success("Tính giá thành công", { id: `calc-${detailId}` });
      refetch();
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi tính giá", { id: `calc-${detailId}` });
    } finally {
      setIsCalculating(null);
    }
  };

  const handleOpenQuotation = (quotationId: string) => {
    navigate({
      to: "/quotations/$quotationId",
      params: { quotationId },
    });
  };

  return (
    <div className="space-y-3 pb-6 p-2 md:p-4">
      {/* ═══ THANH CÔNG CỤ TINH GỌN (Zero Double-Header Rule 3) ═══ */}
      <div className="flex items-center justify-between gap-2 p-2.5 bg-card border border-border rounded-xl shadow-2xs">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs bg-muted/50 font-semibold">
            Bảng điều hành dự toán định mức
          </Badge>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
          className="h-8 text-xs font-medium"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 mr-1.5 ${isLoading ? "animate-spin" : ""}`}
          />
          Làm mới dữ liệu
        </Button>
      </div>

      {/* ═══ 4 THẺ KPI ĐỊNH MỨC & DỰ TOÁN ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiStatCard
          title="Hạng mục chờ tính"
          value={totalItems}
          unit="mục"
          formatType="number"
          colorVariant="blue"
          icon={Calculator}
          badgeText="Cần định mức"
          direction="inverse"
          isLoading={isLoading}
          testId="est-kpi-total"
        />

        <KpiStatCard
          title="Tổng sản lượng đặt"
          value={totalQuantity}
          unit="pcs"
          formatType="number"
          colorVariant="emerald"
          icon={Package}
          direction="positive"
          isLoading={isLoading}
          testId="est-kpi-qty"
        />

        <KpiStatCard
          title="Đang tính giá"
          value={estimatingCount}
          unit="mục"
          formatType="number"
          colorVariant="amber"
          icon={Clock}
          badgeText="Đang xử lý"
          direction="positive"
          isLoading={isLoading}
          testId="est-kpi-estimating"
        />

        <KpiStatCard
          title="Chờ tiếp nhận"
          value={pendingCount}
          unit="mục"
          formatType="number"
          colorVariant="indigo"
          icon={Layers}
          badgeText="Chưa tiếp nhận"
          direction="inverse"
          isLoading={isLoading}
          testId="est-kpi-pending"
        />
      </div>

      {/* ═══ BẢNG KÊ DANH SÁCH CHI TIẾT BÁO GIÁ CẦN TÍNH GIÁ ═══ */}
      <div className="bg-card border border-border rounded-xl shadow-2xs overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-xs text-muted-foreground">
            Đang tải dữ liệu chờ tính giá...
          </div>
        ) : pendingDetails.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center">
            <Calculator className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-xs text-muted-foreground font-medium">
              Không có hạng mục nào đang chờ tính giá. Toàn bộ báo giá đã được cập nhật giá vốn.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead className="w-12 text-center text-xs">STT</TableHead>
                  <TableHead className="text-xs">Sản phẩm</TableHead>
                  <TableHead className="text-xs">Quy cách</TableHead>
                  <TableHead className="text-xs">Báo giá liên kết</TableHead>
                  <TableHead className="text-xs text-right">Số lượng</TableHead>
                  <TableHead className="text-xs text-center">Trạng thái</TableHead>
                  <TableHead className="text-xs text-right">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedDetails.map((detail: any, idx: number) => {
                  let specsText = detail.specs || detail.product?.specs || "";
                  if (typeof specsText === "object") {
                    specsText =
                      specsText.dimensions ||
                      specsText.size ||
                      JSON.stringify(specsText);
                  }
                  const stt = (page - 1) * pageSize + idx + 1;

                  return (
                    <TableRow key={detail.id} className="hover:bg-muted/50 text-xs">
                      <TableCell className="text-center font-mono text-muted-foreground">
                        {stt}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          {detail.product?.image ? (
                            <img
                              src={detail.product.image}
                              alt="SP"
                              className="w-8 h-8 object-contain rounded border border-border"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded border border-border bg-muted/30 flex items-center justify-center shrink-0">
                              <Package className="w-4 h-4 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-foreground">
                              {detail.productName ||
                                detail.product?.productName ||
                                "—"}
                            </div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {detail.productCode ||
                                detail.product?.productCode}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {specsText || "—"}
                      </TableCell>
                      <TableCell>
                        <div
                          className="text-primary font-semibold cursor-pointer hover:underline font-mono"
                          onClick={() =>
                            handleOpenQuotation(detail.quotation.id)
                          }
                        >
                          {detail.quotation?.quotationId || "—"}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {detail.quotation?.customer?.shortName ||
                            detail.quotation?.customer?.name ||
                            ""}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-mono font-bold text-foreground">
                        {formatNumber(detail.quantity)}{" "}
                        <span className="text-[10px] font-normal text-muted-foreground">
                          {detail.unit || "PCS"}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {detail.pricingStatus === "ESTIMATING" ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-amber-500/10 text-amber-700 border-amber-500/20"
                          >
                            Đang tính
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-blue-500/10 text-blue-700 border-blue-500/20"
                          >
                            Chờ tính giá
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCalculate(detail.id)}
                          disabled={isCalculating === detail.id}
                          className="h-7 px-2 text-xs text-primary border-primary/30 hover:bg-primary/10"
                        >
                          {isCalculating === detail.id ? (
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Calculator className="w-3 h-3 mr-1" />
                          )}
                          Tính tự động
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Phân trang ERPPagination */}
        <ERPPagination
          currentPage={page}
          totalPages={Math.max(1, Math.ceil(totalItems / pageSize))}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          unitLabel="hạng mục"
        />
      </div>
    </div>
  );
}
